/**
 * Sequence formats (1.0.0-rc.11): the sizes and frame rates Create offers, shared by the New sequence dialog, the API
 * and the docs. A sequence has no scan type: it is a frame size and a frame rate, and the export format decides
 * whether the file is progressive or interlaced (an interlaced export of a 25 fps sequence is 1080i50).
 */
import { frameRateLabel, type Rational } from './time.ts';

export const SEQUENCE_RATES = {
  24: { num: 24, den: 1 },
  25: { num: 25, den: 1 },
  29.97: { num: 30000, den: 1001 },
  30: { num: 30, den: 1 },
  50: { num: 50, den: 1 },
  59.94: { num: 60000, den: 1001 },
} as const satisfies Record<string, Rational>;

export interface SequenceFormatGroup {
  id: string;
  label: string;
  width: number;
  height: number;
  rates: Rational[];
}

const R = SEQUENCE_RATES;
export const SEQUENCE_FORMAT_GROUPS: SequenceFormatGroup[] = [
  { id: 'hd1080', label: 'HD 1920×1080', width: 1920, height: 1080, rates: [R[25], R[29.97], R[24], R[50], R[59.94]] },
  { id: 'uhd', label: 'UHD 3840×2160', width: 3840, height: 2160, rates: [R[25], R[29.97], R[50]] },
  { id: 'hd720', label: 'HD 1280×720', width: 1280, height: 720, rates: [R[50], R[59.94]] },
  { id: 'vertical', label: 'Vertical 9:16 1080×1920', width: 1080, height: 1920, rates: [R[25], R[29.97], R[30]] },
  { id: 'portrait45', label: 'Portrait 4:5 1080×1350', width: 1080, height: 1350, rates: [R[25], R[29.97], R[30]] },
  { id: 'square', label: 'Square 1:1 1080×1080', width: 1080, height: 1080, rates: [R[25], R[29.97], R[30]] },
];

/** The frame rates a custom size can have: every rate of the formats above. */
export const CUSTOM_SEQUENCE_RATES: Rational[] = [R[24], R[25], R[29.97], R[30], R[50], R[59.94]];

/** The model's limits on a sequence frame (sequence.ts). Sizes are even, as every export format needs. */
export const SEQUENCE_SIZE_LIMITS = { minWidth: 16, maxWidth: 7680, minHeight: 16, maxHeight: 4320 } as const;

/** The interlaced export (1080i) a rate can have, by field rate: 25 and 50 → 1080i50, 29.97 and 59.94 → 1080i59.94. */
export function interlacedName(rate: Rational): string | null {
  const fps = rate.num / rate.den;
  if (Math.abs(fps - 25) < 0.001 || Math.abs(fps - 50) < 0.001) return '1080i50';
  if (Math.abs(fps - 30000 / 1001) < 0.001 || Math.abs(fps - 60000 / 1001) < 0.001) return '1080i59.94';
  return null;
}

export interface SequenceFormatPreset {
  id: string;
  group: string;
  label: string;
  width: number;
  height: number;
  frameRate: Rational;
}

/**
 * Every format, labelled by its frame rate only, e.g. "HD 1920×1080 · 25 fps (25p or 1080i50 at export)". The note
 * on interlacing is shown for landscape 16:9 frames, the ones broadcast formats carry without bars.
 */
export const SEQUENCE_FORMATS: SequenceFormatPreset[] = SEQUENCE_FORMAT_GROUPS.flatMap((g) => g.rates.map((r) => {
  const fps = frameRateLabel(r);
  const i = g.width * 9 === g.height * 16 ? interlacedName(r) : null;
  return { id: `${g.id}-${fps}`, group: g.id, label: `${g.label} · ${fps} fps${i ? ` (${fps}p or ${i} at export)` : ''}`, width: g.width, height: g.height, frameRate: r };
}));

/** Why a custom size cannot be a sequence; null when it can. */
export function sequenceSizeProblem(width: number, height: number): string | null {
  const L = SEQUENCE_SIZE_LIMITS;
  if (!Number.isInteger(width) || !Number.isInteger(height)) return 'The width and height are whole pixels';
  if (width < L.minWidth || width > L.maxWidth) return `The width is ${L.minWidth}–${L.maxWidth} pixels`;
  if (height < L.minHeight || height > L.maxHeight) return `The height is ${L.minHeight}–${L.maxHeight} pixels`;
  if (width % 2 || height % 2) return 'The width and height are even numbers (every export format needs them)';
  return null;
}
